/**
 * LogViewer Component
 * 
 * Composant réutilisable pour afficher des logs avec horodatage
 * Utilisé pour tracer les progressions de traitements longs (indexation, synchronisation, etc.)
 */

import React from 'react';
import PropTypes from 'prop-types';
import './LogViewer.css';

/**
 * Affiche une liste de messages de log avec horodatage
 * 
 * @param {Object} props
 * @param {string} props.title - Titre du viewer (optionnel)
 * @param {Array<string>} props.messages - Tableau de messages de log
 * @param {number} props.maxHeight - Hauteur maximale du conteneur de log (défaut: 300px)
 * @param {boolean} props.autoScroll - Faire défiler automatiquement vers le bas (défaut: true)
 */
export default function LogViewer({ 
  title = 'Log de traitement', 
  messages = [], 
  maxHeight = 300,
  autoScroll = true 
}) {
  const logRef = React.useRef(null);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  React.useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className="log-viewer">
      <h3 className="log-viewer-title">{title}</h3>
      <pre 
        className="log-content" 
        ref={logRef}
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {messages.join('\n')}
      </pre>
    </div>
  );
}

LogViewer.propTypes = {
  title: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.string),
  maxHeight: PropTypes.number,
  autoScroll: PropTypes.bool
};
